import { has, get, isEmpty, cloneDeep, concat, intersection, includes } from "lodash";
import exifr from 'exifr';

import appCfg from "../config";
import C from "../constants";
import attestivNet from "../lib/AttestivNet";
import strings from "../resources/strings";
import exifHelper from "@attestiv/attestiv-ui/util/exif";
import cookieHelper from "../helpers/cookies";
import { getAnalysisTypes } from "../helpers/analysis";

import { isMobile } from "react-device-detect";

const fingerprintModule = require("./fingerprint");


const fileUploadMaxRetries = "fileUploadMaxRetries" in appCfg ? appCfg.fileUploadMaxRetries : 3;

var dispatch;

// Load the WASM connector module
var attestivFingerprint = undefined;
fingerprintModule().then(async instance => {
    console.debug("Loaded fingerprint module " + await instance.get_version());
    attestivFingerprint = instance;
});


let initialize = function(store) {
    dispatch = store.dispatch;
};

let checkForExistingRecord = function(state) {
    return new Promise((resolve, reject) => {
        let recordId;

        if (state.recordId) {
            recordId = state.recordId;
        }
        else if (state.clientRequest && "addToRecord" in state.clientRequest) {
            recordId = state.clientRequest.addToRecord;
        }

        if (recordId) {
            console.debug("Adding media/data to existing record");
            attestivNet.getRecord(recordId)
                .then(function(response) {
                    response && console.debug("Record exists (add to existing record)");
                    dispatch({ type: C.SAVE_RECORD, record: response });
                    resolve(response ? response : null);//
                })
                .catch(function(error) {
                    resolve(null);
                });
            return;
        }

        // If this is a sticky request, we may already have a record for it
        if (state.clientRequest && state.clientRequest.state === "STICKY") {
            console.debug("Adding media/data to existing record from sticky request");
            attestivNet.getRecordWithRequestId(state.clientRequest.requestId)
                .then(function(response) {
                    response.docs.length > 0 && console.debug("Record already exists (sticky)");
                    dispatch({ type: C.SAVE_RECORD, record: response });
                    resolve(response.docs.length > 0 ? response.docs[0] : null);
                })
                .catch(function(error) {
                    resolve(null);
                });
        }
        else {
            resolve(null);
        }
    });
};

let createRecordInfo = function(state) {
    let clientRequest = state.clientRequest;
    let name = strings.getString("inProgressNotComplete");
    let description;

    const hasValue = function(value) {
        return value !== undefined && value !== null && value !== "";
    };

    const getNameFromValues = function(values) {
        const nameValues = values.filter(hasValue);
        return nameValues.length > 0 ? nameValues.join(" ") : null;
    };

    const getRecordNameFromAnswers = function() {
        const questionSet = state.questionSet;
        if (!questionSet || !questionSet.recordNameProperties || questionSet.recordNameProperties.length === 0) {
            return null;
        }

        let values = [];
        let recordNameProps = questionSet.recordNameProperties;
        for (let i = 0; i < recordNameProps.length; i++) {
            let parts = recordNameProps[i].split(".");
            let answers = []
                .concat(get(state, "questionHistory", []))
                .concat(Object.values(get(state, "forwardAnswers", {})));
            let answer = answers.filter(ans => ans && ans.questionID === parts[0])[0];
            let field = answer && answer.fields ? answer.fields.filter(field => field.fieldID === parts[1])[0] : null;
            if (field && hasValue(field.value)) {
                values.push(field.value);
            }
        }
        return getNameFromValues(values);
    };

    // Standard client request with attached name
    if (clientRequest && clientRequest.firstName && clientRequest.lastName) {
        name = clientRequest.firstName + " " + clientRequest.lastName;
        description = clientRequest.notes ? clientRequest.notes : undefined;
    }
    // Client request with dynamic name (probably unused)
    else if (clientRequest && "recordNameProperties" in clientRequest && clientRequest.recordNameProperties &&
        clientRequest.recordNameProperties.length > 0){
        let recordNameProps = clientRequest.recordNameProperties;
        let values = [];
        if (recordNameProps.length > 0) {
            for (let i = 0; i < recordNameProps.length; i++) {
                values.push(clientRequest[recordNameProps[i]]);
            }
        }
        name = getNameFromValues(values) || name;
        description = clientRequest.notes ? clientRequest.notes : undefined;
    }
    else {
        name = getRecordNameFromAnswers() || name;
    }

    return {name: name, description: description};
};

let ensureRecord = function(state) {
    let promise = new Promise((resolve, reject) => {
        checkForExistingRecord(state).then(function(record) {
            if (record) {
                console.debug("Record exists: " + record.referenceId);
                if (!has(state, "recordId")) {
                    dispatch({ type: C.CREATE_SVR_RECORD, recordId: record.referenceId }); // add recordId to state
                }
                resolve(record.referenceId);
            }
            else {
                console.debug("Creating record");
                let {name, description} = createRecordInfo(state);

                attestivNet.createRecord(name, state.questionSet.name, description, state.requestId, state.recordId)
                    .then(function(response) {
                        console.debug(response);
                        console.debug("Record Created");

                        dispatch({ type: C.CREATE_SVR_RECORD, recordId: response.referenceId }); // add recordId to state
                        resolve(response.referenceId);

                        attestivNet.updateWorkflowSession(state, null, response.referenceId)
                            .then((response) => {
                                console.log(response);
                                if ("status" in response && response.status === "Failure") {
                                    console.error(response.error);
                                }
                            })
                            .catch((error) => {
                                console.error(error);
                            });
                    })
                    .catch(function(error) {
                        // A race condition may occur where more than one request to check if the record exists may happen,
                        // if that does we will get this code back and we can safely ignore it.
                        if (get(error, "response.data.code") === 5005) {
                            let recordId = get(state, "recordId");
                            resolve(recordId ? recordId : undefined);
                            return;
                        }
                        alert(strings.getString("recordCreationFailed", error.response.data.message));
                        reject();
                    });
            }
        });
    });

    return promise;
}

function constructMediaJSONObject(state, answer, tamperedResult, objectsDetectedResult, damageDetectedResult, facialDetectionResult, media, tags, uploadLocation, arrayBuffer, width, height) {
    return new Promise(async (resolve, reject) => {
        let fingerprint = media.isJPEG ? await attestivFingerprint.fingerprint_jpg(arrayBuffer) : await attestivFingerprint.fingerprint_file(media);
        if (!fingerprint) {
            console.debug("Invalid media fingerprint calculation: " + media.name);
            reject();
        }

        let fileFingerprint;
        if (media.isJPEG) {
            fileFingerprint = await attestivFingerprint.fingerprint_file(media);
            if (!fileFingerprint) {
                console.debug("Invalid file fingerprint calculation: " + media.name);
                reject();
            }
        }

        let exifData;
        if (media.isJPEG) {
            exifData = await exifr.parse(media, {xmp: true, icc: true, iptc: true, jfif: true, ihdr: true});
            if (exifData) {
                console.debug("EXIF: " + JSON.stringify(exifData, null, 4));
            }
            else {
                console.debug(`Could not read exif data for file ${media.uuid}`)
            }
        }

        let mediaType = media.type;
        if (!media.isJPEG && mediaType.startsWith("image/")) {
            // This is a special case. If it is not a jpg but the mimeType claims it is
            // then set width and height to 1 and 1 because size is required for images
            width = 1;
            height = 1;
        }

        let mediaItem = {
            workflowSessionId: state.workflowSession.workflowSessionId,
            questionId: answer.questionID,
            uuid: media.uuid,
            mimeType: mediaType,
            analysis: cloneDeep(answer.analysis) || [],
            fingerprint: fingerprint,
            size: width && height ? width + "x" + height : undefined,
            parentUUID: undefined,
            tags: cloneDeep(tags) || [],
            timestamp: media.lastModified
        };

        if (fileFingerprint) {
            mediaItem.fileFingerprint = fileFingerprint;
        }

        if (media.isJPEG) {
            mediaItem.isJPEG = media.isJPEG;
        }

        if (exifData) {
            exifHelper.extractExifData(exifData, mediaItem);
        }

        exifHelper.setIfNotEmpty(mediaItem, "tamperedResult", tamperedResult);
        exifHelper.setIfNotEmpty(mediaItem, "objectsDetectedResult", objectsDetectedResult);
        exifHelper.setIfNotEmpty(mediaItem, "damageDetectedResult", damageDetectedResult);
        exifHelper.setIfNotEmpty(mediaItem, "facialDetectionResult", facialDetectionResult);
        exifHelper.setIfNotEmpty(mediaItem, "searchTerms", answer.searchTerms);

        // on iOS we cannot get the location data from exif (privacy), so we use the app's location if they are on a phone or tablet (isMobile).
        if (!has(mediaItem, "location") && isMobile && !includes(tags, "import")) {
            navigator.geolocation.getCurrentPosition(
                function(location) {
                    mediaItem.location = get(location.coords, "latitude") + "," + get(location.coords, "longitude");
                    resolve(mediaItem);
                },
                function(positionError) {
                    switch (positionError.code) {
                        case positionError.PERMISSION_DENIED: console.warn(strings.getString("locationPermissionDenied", positionError.message)); break;
                        case positionError.POSITION_UNAVAILABLE: console.warn(strings.getString("locationPositionUnavailable", positionError.message)); break;
                        case positionError.TIMEOUT: console.warn(strings.getString("locationTimeout", positionError.message)); break;
                        default: console.warn(strings.getString("locationUnavailable", positionError.message)); break;
                    }

                    if (!has(mediaItem, "location") && uploadLocation && isMobile) {
                        mediaItem.location = get(uploadLocation.coords, "latitude") + "," + get(uploadLocation.coords, "longitude");
                    }
            
                    resolve(mediaItem);
                },
                {
                    timeout: "geolocation" in appCfg && "timeout" in appCfg.geolocation ? appCfg.geolocation.timeout : 15000,
                    enableHighAccuracy: true,
                    maximumAge: 0
                }
            );
        }
        else {
            if (!has(mediaItem, "location") && uploadLocation && isMobile && !includes(tags, "import")) {
                mediaItem.location = get(uploadLocation.coords, "latitude") + "," + get(uploadLocation.coords, "longitude");
            }
    
            resolve(mediaItem);
        }
    });
}

// not used for images
function getMediaTypeTag(mediaType) {
    if (mediaType === "application/pdf") {
        return "pdf";
    }
    else if (mediaType.startsWith("video/")) {
        return "video";
    }
    else if (mediaType.startsWith("audio/")) {
        return "audio";
    }
    else {
        return "otherFileType";
    }
}

function createMediaJSON(state, tamperedResult, objectsDetectedResult, damageDetectedResult, facialDetectionResult, media, tags, answer) {
    console.log(media);

    let location = "location" in state && state.location ? state.location : undefined;

    // if not a JPEG, create a media object and add the media type tag to the tags array
    if (!media.isJPEG) {
        return new Promise((resolve, reject) => {
            constructMediaJSONObject(state, answer, tamperedResult, objectsDetectedResult, damageDetectedResult, facialDetectionResult, media, tags, location)
            .then((mediaObj) => {
                let type = getMediaTypeTag(media.type);
                if (mediaObj && type) {
                    mediaObj.tags.push(type);
                    resolve(mediaObj);
                }
                else {
                    let msg = strings.getString("mediaNotAddedToMetadata");
                    console.error(msg);
                    alert(msg);
                    reject(mediaObj);
                }
            })
            .catch((error) => {
                console.error(strings.getString("mediaItemNotAddedToMetadata"));
                reject();
            });
        })
    }

    // if JPEG, read the file into a file reader, and then create the media metadata
    return new Promise(function(resolve, reject) {
        let fr = new FileReader();
        fr.onload = async function(evt) {
            console.debug("FileReader result: " + evt.target.result);
            var img = new Image();
            img.onload = async function() {
                // Create the media metadata
                console.debug(this.name + ": " + this.width + "x" + this.height);
                constructMediaJSONObject(state, answer, tamperedResult, objectsDetectedResult, damageDetectedResult, facialDetectionResult, media, tags, location, evt.target.result, this.width, this.height)
                    .then((mediaObj) => {
                        if (mediaObj) {
                            console.debug("full size uuid: " + mediaObj.uuid);
                            mediaObj.tags.push("full-scale");
                            resolve(mediaObj);
                        }
                        else {
                            let msg = strings.getString("imageNotAddedToMetadata");
                            console.error(msg);
                            alert(msg);
                            reject();
                        }
                    });
            };
            img.src = window.URL.createObjectURL(media);
        }
        fr.readAsArrayBuffer(media);
    });
}

// Retry the upload up to fileUploadMaxRetries
function handleUploadError(error, media, recordId, state, answer, retryCount) {
    console.error(error);

    retryCount++;
    if (retryCount <= fileUploadMaxRetries) {
        console.debug("Retrying..." + retryCount);
        return uploadFile(media, recordId, state, answer, retryCount);
    } else {
        dispatch({ type: C.DECREMENT_FILE_UPLOAD_COUNTER, success: false });
        return Promise.reject("Upload failed: " + media.name);
    }
}

function uploadFile(file, recordId, state, answer, retryCount) {
    if (!retryCount) {
        retryCount = 0;
    }

    console.debug(`analysis: ${answer.analysis}`);

    return new Promise(function(resolve, reject) {
        attestivNet
            .uploadMedia(file, recordId, file.uuid, answer.analysis, answer.triageType, answer.models)
            .then(function(response) {
                console.log(response);
                if (response.result === "Success") {
                    console.debug(JSON.stringify(response));
                    console.debug("Done Uploading: " + file.name);
                    dispatch({ type: C.DECREMENT_FILE_UPLOAD_COUNTER, success: true });
                    dispatch({ type: C.SAVE_FILEPATH, uuid: file.uuid, filepath: response.filepath });
                    resolve(file);
                } else {
                    return handleUploadError(response.error, file, recordId, state, answer, retryCount).catch(reject);
                }
            })
            .catch(function(error) {
                handleUploadError(error, file, recordId, state, answer, retryCount)
                    .catch(async function() {
                        if (retryCount === fileUploadMaxRetries) {
                            cookieHelper.removeCookie(state.referenceId);
                            await attestivNet.updateRecord({referenceId: recordId, state: "ABANDONED"})
                            alert(strings.getString("fileUploadFailedRetryLater"));
                            window.location.reload();
                        }
                        reject();
                    });
            });
    });
}

function uploadMediaMetadata(mediaJSON) {
    return new Promise(function(resolve, reject) {
        attestivNet
            .uploadMediaMetadata(mediaJSON)
            .then(function(response) {
                console.log(response);
                if (response.status === "Success") {
                    console.debug("Upload media metadata result: " + JSON.stringify(response));
                    resolve(mediaJSON, response);
                }
                else {
                    console.error(strings.getString("mediaMetadataFailedToUpload"));
                    reject(mediaJSON);
                }
            })
            .catch(function(error) {
                console.error(strings.getString("mediaMetadataFailedToUpload"));
                reject(mediaJSON);
            });
    });
}

function addAnalysisTypeToTags(tags, analysis) {
    analysis = getAnalysisTypes(analysis);
    // In uploadMedia, we have to make sure that the analysis tags aren't copied over twice.
    let inCommon = intersection(tags, analysis);
    if (get(inCommon, 'length') === 0) {
        return !isEmpty(tags) ? concat(tags, analysis) : concat([], analysis);
    }
    return tags;
}

let uploadMedia = function(recordId, answer, state) {
    // if there are any tampered files, upload those first
    if (has(answer, "tamperedMedia")) {
        answer.tamperedMedia.forEach(tamperedFile => {
            if (has(tamperedFile, "file")) {
                tamperedFile.tags = addAnalysisTypeToTags(tamperedFile.tags, answer.analysis);
                createMediaJSON(state, tamperedFile.tamperedResult, tamperedFile.objectsDetectedResult, tamperedFile.damageDetectedResult, answer.facialDetectionResult, tamperedFile.file, tamperedFile.tags, answer)
                    .then((mediaObj) => {
                        tamperedFile.mediaJSON = mediaObj;
                        uploadMediaMetadata(tamperedFile.mediaJSON)
                            .then(() => {
                                console.debug(`analysis: ${answer.analysis}`);
                                uploadFile(tamperedFile.file, recordId, state, answer);
                            });
                    });
            }
        });
    }

    if (has(answer, "file")) {
        answer.tags = addAnalysisTypeToTags(answer.tags, answer.analysis);
        createMediaJSON(state, answer.tamperedResult, answer.objectsDetectedResult, answer.damageDetectedResult, answer.facialDetectionResult, answer.file, answer.tags, answer)
            .then((mediaObj) => {
                answer.mediaJSON = mediaObj;
                uploadMediaMetadata(answer.mediaJSON)
                    .then(() => {
                        console.debug(`analysis: ${answer.analysis}`);
                        uploadFile(answer.file, recordId, state, answer);
                    });
            });
    }
};

let uploadWorkflowData = function(recordId, answer, state) {
    return new Promise(function(resolve, reject) {
        if (isEmpty(answer) || !answer.fields) {
            resolve();
            return;
        }

        let workflowData = [];
        state.questionHistory.forEach(element => {
            let wd = {
                questionID: element.questionID,
                questionType: element.questionType,
                fileType: element.fileType,
                filepath: element.filepath,
                fields: element.fields
            };
            wd.fields = element.fields && element.fields.length > 0 ? element.fields : [];

            workflowData.push(wd);
        });

        attestivNet.uploadWorkflowData(workflowData, recordId, state)
            .then(function(response) {
                console.debug(response);
                console.debug("Uploaded workflow data.");
                resolve(response);
            })
            .catch(function(error) {
                console.debug("Workflow data upload failed. " + JSON.stringify(error));
                alert(strings.getString("dataUploadFailed"));
                reject(error);
            });
    });
}

let uploadBillableOperationsData = function(state) {
    return new Promise(function(resolve, reject) {
        let ops = get(state, "billableOperations");
        if (!ops) {
            return;
        }

        attestivNet.updateRecord({
            referenceId: state.recordId,
            billableOperations: ops
        }).then(function(response) {
                console.debug(response);
                console.debug("Uploaded billable operations data.");
                resolve(response);
            })
            .catch(function(error) {
                console.debug("Billable operations data upload failed. " + JSON.stringify(error));
                reject(error);
            });
    });
}

export default {
    checkForExistingRecord,
    createRecordInfo,
    ensureRecord,
    initialize,
    uploadMedia,
    uploadBillableOperationsData,
    uploadWorkflowData
};
